import { useState, useEffect, useRef, memo } from "react";
import styles from "./DataGridNews.module.scss";
import { Virtuoso } from "react-virtuoso";
import { useAtomValue } from "jotai";
import { Box, CircularProgress, Typography, useMediaQuery } from "@mui/material";
import Skeleton from "@/components/skeleton/Skeleton";
import { getDataGridNews } from "@/controllers/news";
import { openNewsStory } from "@/components/news-story-modal/NewsStoryModal";
import { Stock } from "@/models/stock";
import { textColorState } from "@/lib/store";
import NewsCard from "./NewsCard";
import SmallObfuscationBanner from "../obfuscation/SmallObfuscationBanner";

type Props = {
    amount_news?: { desktop: number; mobile: number; "4k": number };
    custom_height?: string;
    header_layout?: string;
    id: number;
    mobile_design?: boolean;
    options: Record<string, any>;
    regionId?: number;
    showStock?: boolean;
    sortBy?: string;
    stock?: Stock;
};

const NewsListFooter = ({ context }: { context?: { loadingMore: boolean } }) => {
    if (!context?.loadingMore) return null;
    return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", paddingY: 2 }}>
            <CircularProgress />
        </Box>
    );
};

export const DataGridNews = ({ custom_height, header_layout = "30% 20% 50%", id, mobile_design, options, regionId, showStock, sortBy, stock }: Props) => {
    const textColor = useAtomValue(textColorState);

    const [newsList, setNewsList] = useState([]);
    const [obfuscated, setObfuscated] = useState(false);
    const [noObfuscatedRowsQty, setNoObfuscatedRowsQty] = useState(0);

    const [isLoading, setIsLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);

    const isMobile = useMediaQuery("(max-width: 768px)");
    const is4k = useMediaQuery("(min-width: 3840px)");

    const newsAmount = isMobile ? 8 : is4k ? 25 : 20;
    const page = useRef(1);
    const totalPages = useRef(1);
    const finalId = stock?.company_id || regionId || id;
    const nextPageAvailable = useRef(true);

    const apiParams = sortBy === "importance" ? { sortModel: { priority: "desc", published_at: "desc" } } : {};

    const scrollerRef = useRef<HTMLElement | null>(null);

    const [sourceTooltip, setSourceTooltip] = useState<string>("");
    const [ratingTooltip, setRatingTooltip] = useState<string>("");

    const getNewsDetail = (id: string) => openNewsStory(id);

    const fetchNews = (loadMore: boolean) => {
        if (finalId === undefined || !nextPageAvailable.current) return;
        if (!loadMore) setIsLoading(true);
        else setLoadingMore(true);

        getDataGridNews({ page_size: newsAmount, page: page.current, filter_options: apiParams, id: finalId, data: options })
            .then((res) => {
                if (res.paginationLinks && res.paginationLinks.next === null) nextPageAvailable.current = false;
                else nextPageAvailable.current = true;

                if (res.obfuscation?.obfuscated) {
                    setObfuscated(true);
                    setNoObfuscatedRowsQty(res?.obfuscation?.doNotObfuscateRows || 0);
                    nextPageAvailable.current = false;
                    delete res?.obfuscation?.obfuscated;
                }

                // A response without rows adds nothing: spreading it crashed the whole page (seen on
                // the Index Rebalance trade page at 3840px, signed out).
                const rows = Array.isArray(res?.rows) ? res.rows : [];
                if (loadMore) {
                    setNewsList((prevState) => [...(prevState ?? []), ...rows]);
                } else {
                    totalPages.current = Math.ceil(res.length / 30);
                    setNewsList(rows);
                }
            })
            // A failed or timed-out page leaves the list as it was, instead of an unhandled rejection
            // (an AxiosError on the console whenever the news endpoint is slow). A failed extra page
            // steps the counter back, so the next scroll asks for that page again rather than skip it.
            .catch(() => {
                if (loadMore) page.current = Math.max(1, page.current - 1);
            })
            .finally(() => {
                setIsLoading(false);
                setLoadingMore(false);
            });
    };

    useEffect(() => {
        page.current = 1;
        nextPageAvailable.current = true;
        fetchNews(false);

        const closeTooltips = () => {
            setSourceTooltip("");
            setRatingTooltip("");
        };
        // The scroller mounts after the first render; wait for it (up to 2 s) and remember it to remove the listener.
        let container: HTMLElement | null = null;
        const interval = setInterval(() => {
            if (!scrollerRef.current) return;

            container = scrollerRef.current;
            container.addEventListener("scroll", closeTooltips);
            clearInterval(interval);
        }, 100);

        const stopWaiting = setTimeout(() => clearInterval(interval), 2000);

        return () => {
            clearInterval(interval);
            clearTimeout(stopWaiting);
            container?.removeEventListener("scroll", closeTooltips);
            setNewsList([]);
        };
    }, [stock, regionId, id]);

    useEffect(() => {
        // Automatically load more news if the container's content doesn't fill its visible height on first render
        // This ensures that the news list fills the container without empty space on initial load
        if (!isLoading && page.current === 1 && scrollerRef.current && nextPageAvailable.current && !obfuscated) {
            const el = scrollerRef.current;
            if (el.scrollHeight <= el.clientHeight) {
                loadMoreNews();
            }
        }
    }, [isLoading]);

    const loadMoreNews = () => {
        if (!loadingMore && nextPageAvailable.current) {
            page.current++;
            fetchNews(true);
        }
    };

    if (isLoading || id === undefined) {
        return <Skeleton variant="rounded" width="100%" height="100%" loading={isLoading || id === undefined} />;
    }

    return (
        <Box className={styles.NewNewsList}>
            <Box className={styles.container} style={{ height: custom_height || undefined, overflow: obfuscated ? "hidden" : undefined }}>
                {newsList?.length === 0 ? (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                        <Typography variant="overline" component="p" sx={{ color: textColor }} className={styles.noNewsText}>
                            No News Available
                        </Typography>
                    </Box>
                ) : (
                    <Virtuoso
                        style={{ height: "100%", overscrollBehavior: "contain" }}
                        data={newsList}
                        endReached={obfuscated ? undefined : loadMoreNews}
                        scrollerRef={(ref) => {
                            scrollerRef.current = ref as HTMLElement;
                        }}
                        context={{ loadingMore }}
                        components={{ Footer: NewsListFooter }}
                        itemContent={(index, news) => {
                            const isObfuscated = obfuscated && index >= noObfuscatedRowsQty;
                            const isFirstObfuscated = isObfuscated && index === noObfuscatedRowsQty;
                            return (
                                <Box key={news.id} sx={{ position: "relative" }}>
                                    {isFirstObfuscated && <SmallObfuscationBanner />}
                                    <NewsCard
                                        news={news}
                                        showStock={showStock}
                                        obfuscated={isObfuscated}
                                        mobile_design={mobile_design}
                                        header_layout={header_layout}
                                        getNewsDetail={isObfuscated ? () => {} : getNewsDetail}
                                        sourceTooltip={sourceTooltip}
                                        setSourceTooltip={setSourceTooltip}
                                        ratingTooltip={ratingTooltip}
                                        setRatingTooltip={setRatingTooltip}
                                    />
                                </Box>
                            );
                        }}
                    />
                )}
            </Box>
        </Box>
    );
};

export const MemoDataGridNews = memo(DataGridNews);
